'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Lock, LogIn, Mail } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiOutlineSwitchHorizontal } from 'react-icons/hi';

import { useEnterpriseFetchTenants, useLogin } from '@/hooks/mutations/auth';

import { TenantWakingOverlay } from '@/components/auth/TenantWakingOverlay';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { createLoginSchema, type LoginSchema } from '@/schema/auth';

export function LoginForm() {
  const t = useTranslations('AuthLogin');
  const tVal = useTranslations('AuthLogin.validation');
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [enterpriceLogin, setEnterpriceLogin] = useState(false);
  const [enterpriseEmail, setEnterpriseEmail] = useState('');
  const [enterpriseEmailError, setEnterpriseEmailError] = useState('');
  const [enterprisePassword, setEnterprisePassword] = useState('');
  const [enterprisePasswordError, setEnterprisePasswordError] = useState('');
  const [showEnterprisePassword, setShowEnterprisePassword] = useState(false);
  const loginMutation = useLogin();
  const enterpriseFetch = useEnterpriseFetchTenants();

  const loginSchema = useMemo(
    () =>
      createLoginSchema({
        emailRequired: tVal('emailRequired'),
        emailInvalid: tVal('emailInvalid'),
        passwordRequired: tVal('passwordRequired'),
        passwordMin: tVal('passwordMin'),
      }),
    [tVal],
  );

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = (data: LoginSchema) => {
    loginMutation.mutate(data);
  };

  const onEnterpriseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let valid = true;
    if (!enterpriseEmail || !emailRegex.test(enterpriseEmail)) {
      setEnterpriseEmailError(tVal('enterpriseEmailInvalid'));
      valid = false;
    } else {
      setEnterpriseEmailError('');
    }
    if (!enterprisePassword) {
      setEnterprisePasswordError(tVal('enterprisePasswordRequired'));
      valid = false;
    } else {
      setEnterprisePasswordError('');
    }
    if (!valid) return;
    enterpriseFetch.mutate({
      email: enterpriseEmail,
      password: enterprisePassword,
    });
  };

  useEffect(() => {
    const email = searchParams?.get('email');
    const password = searchParams?.get('password');
    if (email && password) {
      loginMutation.mutate({ email, password });
    }
  }, []);

  return (
    <>
      {loginMutation.waking && <TenantWakingOverlay />}
      <Card className='mx-auto w-full max-w-md'>
      <div className='flex border-b'>
        <button
          type='button'
          onClick={() => setEnterpriceLogin(false)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            !enterpriceLogin
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('tabLogin')}
        </button>
        <button
          type='button'
          onClick={() => setEnterpriceLogin(true)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            enterpriceLogin
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('tabEnterprise')}
        </button>
      </div>

      {!enterpriceLogin ? (
        <>
          <CardHeader className='space-y-1 text-center'>
            <CardTitle className='flex items-center justify-center gap-2 text-2xl font-bold'>
              <LogIn className='h-6 w-6' />
              {t('welcomeBack')}
            </CardTitle>
            <CardDescription>{t('enterCredentialsAccount')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='space-y-4'
              >
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('email')}</FormLabel>
                      <FormControl>
                        <div className='relative'>
                          <Mail className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                          <Input
                            type='email'
                            placeholder={t('placeholderEmail')}
                            className='pl-10'
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('password')}</FormLabel>
                      <FormControl>
                        <div className='relative'>
                          <Lock className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('placeholderPassword')}
                            className='pl-10 pr-10'
                            {...field}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword(!showPassword)}
                            className='absolute right-3 top-3 text-muted-foreground hover:text-foreground'
                          >
                            {showPassword ? (
                              <EyeOff className='h-4 w-4' />
                            ) : (
                              <Eye className='h-4 w-4' />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {loginMutation.error && (
                  <Alert variant='destructive'>
                    <AlertDescription>
                      {loginMutation.error.message}
                    </AlertDescription>
                  </Alert>
                )}
                <Button
                  type='submit'
                  className='w-full'
                  isLoading={loginMutation.isPending}
                >
                  {loginMutation.isPending ? t('signingIn') : t('signIn')}
                </Button>
              </form>
            </Form>
          </CardContent>
        </>
      ) : (
        <>
          <CardHeader className='space-y-1 text-center'>
            <CardTitle className='flex items-center justify-center gap-2 text-2xl font-bold'>
              <HiOutlineSwitchHorizontal className='h-6 w-6' />
              {t('enterpriseTitle')}
            </CardTitle>
            <CardDescription>{t('enterpriseSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onEnterpriseSubmit} className='space-y-4'>
              <div className='space-y-2'>
                <label
                  className='text-sm font-medium'
                  htmlFor='enterprise-email'
                >
                  {t('email')}
                </label>
                <div className='relative'>
                  <Mail className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                  <Input
                    id='enterprise-email'
                    type='email'
                    placeholder={t('placeholderWorkEmail')}
                    className='pl-10'
                    value={enterpriseEmail}
                    onChange={(e) => {
                      setEnterpriseEmail(e.target.value);
                      if (enterpriseEmailError) setEnterpriseEmailError('');
                    }}
                    autoComplete='email'
                  />
                </div>
                {enterpriseEmailError && (
                  <p className='text-sm text-destructive'>
                    {enterpriseEmailError}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <label
                  className='text-sm font-medium'
                  htmlFor='enterprise-password'
                >
                  {t('password')}
                </label>
                <div className='relative'>
                  <Lock className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
                  <Input
                    id='enterprise-password'
                    type={showEnterprisePassword ? 'text' : 'password'}
                    placeholder={t('placeholderPassword')}
                    className='pl-10 pr-10'
                    value={enterprisePassword}
                    onChange={(e) => {
                      setEnterprisePassword(e.target.value);
                      if (enterprisePasswordError)
                        setEnterprisePasswordError('');
                    }}
                    autoComplete='current-password'
                  />
                  <button
                    type='button'
                    onClick={() =>
                      setShowEnterprisePassword(!showEnterprisePassword)
                    }
                    className='absolute right-3 top-3 text-muted-foreground hover:text-foreground'
                  >
                    {showEnterprisePassword ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </button>
                </div>
                {enterprisePasswordError && (
                  <p className='text-sm text-destructive'>
                    {enterprisePasswordError}
                  </p>
                )}
              </div>
              {enterpriseFetch.error && (
                <Alert variant='destructive'>
                  <AlertDescription>
                    {enterpriseFetch.error.message}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                type='submit'
                className='w-full'
                isLoading={enterpriseFetch.isPending}
              >
                {enterpriseFetch.isPending
                  ? t('findingWorkspaces')
                  : t('findWorkspaces')}
              </Button>
            </form>
          </CardContent>
        </>
      )}
      </Card>
    </>
  );
}
